import { useMemo } from 'react'
import { Cell, Column } from 'react-table'
import { Badge, Button, HStack } from '@ritualco/jutsu'
import { BasicTable } from 'components/BasicTable'
import { useContent } from 'lib/content/react'
import { WorkspaceConnection } from 'lib/generated/api'

type ActionsProps = {
  connection: WorkspaceConnection
  onResend: () => void
  onRemove: () => void
}

export type CompaniesTableProps = {
  workspaceConnections?: WorkspaceConnection[]
  onResend: (connectionId: string) => void
  onRemove: (connectionId: string) => void
}

const Actions = ({ connection, onResend, onRemove }: ActionsProps) => {
  const { formatMessage } = useContent()
  const showResendButton = connection.status === 'PENDING'
  const showDeleteButton = connection.status !== 'REVOKED'

  return (
    <HStack spacing={2} justifyContent="flex-end">
      {showResendButton && (
        <Button variant="ghost" size="sm" onClick={onResend}>
          {formatMessage({ id: 'pmportal.companies.resend' })}
        </Button>
      )}
      {showDeleteButton && (
        <Button
          colorScheme="transparent"
          variant="outline"
          size="sm"
          onClick={onRemove}
        >
          {formatMessage({ id: 'pmportal.global.actionRemove' })}
        </Button>
      )}
    </HStack>
  )
}

export const CompaniesTable = ({
  workspaceConnections = [],
  onResend,
  onRemove,
}: CompaniesTableProps) => {
  const { formatMessage } = useContent()
  const columns = useMemo<Column<WorkspaceConnection>[]>(
    () => [
      {
        Header: formatMessage({ id: 'pmportal.companies.company' }),
        id: 'connectionId',
        accessor: row => row.invitee?.organizationName,
      },
      {
        Header: formatMessage({ id: 'pmportal.companies.contact' }),
        id: 'contact',
        accessor: row => row.invitee?.entityLabel,
      },
      {
        Header: formatMessage({ id: 'pmportal.companies.status' }),
        accessor: 'status',
        Cell: ({ value }) => {
          if (value === 'PENDING') {
            return (
              <Badge colorScheme="gray">
                {formatMessage({ id: 'pmportal.companies.pendingStatus' })}
              </Badge>
            )
          } else if (value === 'APPROVED' || value === 'CONFIRMED') {
            return (
              <Badge>
                {formatMessage({ id: 'pmportal.companies.acceptedStatus' })}
              </Badge>
            )
          } else if (value === 'REVOKED') {
            return (
              <Badge colorScheme="red" variant="outline">
                {formatMessage({ id: 'pmportal.companies.deletedStatus' })}
              </Badge>
            )
          } else {
            // value === 'expired'
            return (
              <Badge colorScheme="red">
                {formatMessage({ id: 'pmportal.companies.expiredStatus' })}
              </Badge>
            )
          }
        },
      },
      {
        id: 'actions',
        Cell: ({ row }: Cell<WorkspaceConnection>) => (
          <Actions
            connection={row.original}
            onResend={() => {
              onResend(row.original.connectionId)
            }}
            onRemove={() => {
              onRemove(row.original.connectionId)
            }}
          />
        ),
      },
    ],
    [formatMessage, onResend, onRemove]
  )

  return (
    <BasicTable
      columns={columns}
      data={workspaceConnections}
      placeholder={formatMessage({
        id: 'pmportal.global.emptyTable',
      })}
      tdProps={() => ({ lineHeight: 'inherit' })}
    />
  )
}
